import { isJsonObject } from "@/types/json"

interface MetricOutputBoundarySource {
  result?: Record<string, unknown> | null
  result_json?: Record<string, unknown> | null
  runtime_metadata?: Record<string, unknown> | null
  metric_version_id?: string | null
  metric_version_ulid?: string | null
  metric_version_number?: number | null
}

interface CurrentMetricVersion {
  current_version_id?: string | null
  current_version_number?: number | null
}

export type { MetricOutputBoundarySource }

export interface KnowledgeBaseEntryLike {
  entry_id?: string
  kb_identifier?: string
  id?: string
  entry_name?: string
  title?: string
  source?: string
}

function getRecordField(
  record: Record<string, unknown> | null | undefined,
  key: string,
): Record<string, unknown> | undefined {
  const value = record?.[key]
  return isJsonObject(value) ? value : undefined
}

function getRecordArrayField<T extends object>(
  record: Record<string, unknown> | null | undefined,
  key: string,
): T[] {
  const value = record?.[key]
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter(isJsonObject) as T[]
}

export function getMetricResultString(
  metricOutput: MetricOutputBoundarySource,
  key: string,
): string | undefined {
  // Check top-level result first, then nested result.llm for LLM-specific fields
  const value =
    metricOutput.result?.[key] ??
    getLlmField(metricOutput, key) ??
    metricOutput.result_json?.[key] ??
    getLlmFieldFromResultJson(metricOutput, key)
  return typeof value === "string" ? value : undefined
}

function getLlmField(
  metricOutput: MetricOutputBoundarySource,
  key: string,
): unknown {
  const llm = getRecordField(metricOutput.result, "llm")
  if (!llm) {
    return undefined
  }
  // Map legacy field names to LlmContext fields
  if (key === "explanation" || key === "answer_explanation") {
    return llm.answer_explanation
  }
  return llm[key]
}

function getLlmFieldFromResultJson(
  metricOutput: MetricOutputBoundarySource,
  key: string,
): unknown {
  const llm = getRecordField(metricOutput.result_json, "llm")
  if (!llm) {
    return undefined
  }
  if (key === "explanation" || key === "answer_explanation") {
    return llm.answer_explanation
  }
  return llm[key]
}

export function getMetricRuntimeString(
  metricOutput: MetricOutputBoundarySource,
  key: string,
): string | undefined {
  const value = metricOutput.runtime_metadata?.[key]
  return typeof value === "string" ? value : undefined
}

export function getMetricExecutionQuestion(
  metricOutput: MetricOutputBoundarySource,
): string | undefined {
  const llm = getRecordField(metricOutput.result, "llm")
  const promptFromResult = llm?.prompt
  if (typeof promptFromResult === "string" && promptFromResult) {
    return promptFromResult
  }
  // Fallback for old metric outputs without structured result
  return (
    getMetricResultString(metricOutput, "question") ??
    getMetricResultString(metricOutput, "user_provided_prompt") ??
    getMetricResultString(metricOutput, "user_provided_prompt_with_attributes")
  )
}

export function isMetricExecutionConfigCurrent(
  currentQuestion: string | undefined,
  metricOutput: MetricOutputBoundarySource,
  currentVersion?: CurrentMetricVersion,
): boolean {
  const executionVersionId =
    metricOutput.metric_version_id ?? metricOutput.metric_version_ulid
  if (currentVersion?.current_version_id && executionVersionId) {
    return currentVersion.current_version_id === executionVersionId
  }
  const normalizedCurrentQuestion = currentQuestion || ""
  const executionQuestion = getMetricExecutionQuestion(metricOutput)
  if (!normalizedCurrentQuestion || !executionQuestion) {
    return true
  }
  return normalizedCurrentQuestion === executionQuestion
}

export function getMetricExecutionConfigDiff(
  currentQuestion: string | undefined,
  metricOutput: MetricOutputBoundarySource,
  currentVersion?: CurrentMetricVersion,
): { field: string; current: string; previous: string } | undefined {
  const executionVersionId =
    metricOutput.metric_version_id ?? metricOutput.metric_version_ulid
  if (currentVersion?.current_version_id && executionVersionId) {
    if (currentVersion.current_version_id === executionVersionId) {
      return undefined
    }
    const hasVersionNumbers =
      currentVersion.current_version_number != null &&
      metricOutput.metric_version_number != null
    return {
      field: "Version",
      current: hasVersionNumbers
        ? `v${currentVersion.current_version_number}`
        : currentVersion.current_version_id,
      previous: hasVersionNumbers
        ? `v${metricOutput.metric_version_number}`
        : executionVersionId,
    }
  }
  const normalizedCurrentQuestion = currentQuestion || ""
  const executionQuestion = getMetricExecutionQuestion(metricOutput)
  if (
    !normalizedCurrentQuestion ||
    !executionQuestion ||
    normalizedCurrentQuestion === executionQuestion
  ) {
    return undefined
  }

  return {
    field: "Question",
    current: normalizedCurrentQuestion,
    previous: executionQuestion,
  }
}

export function getMetricExplanation(
  metricOutput: MetricOutputBoundarySource,
): string | undefined {
  return getMetricResultString(metricOutput, "explanation")
}

export function getMetricAnswerExplanation(
  metricOutput: MetricOutputBoundarySource,
): string | undefined {
  return getMetricResultString(metricOutput, "answer_explanation")
}

export function getMetricConfidence(
  metricOutput: MetricOutputBoundarySource,
): "high" | "medium" | "low" | undefined {
  const confidence =
    metricOutput.result?.confidence ?? metricOutput.result_json?.confidence
  return confidence === "high" ||
    confidence === "medium" ||
    confidence === "low"
    ? confidence
    : undefined
}

export function getKnowledgeBaseEntries(
  metricOutput: MetricOutputBoundarySource,
): {
  connectedEntries: KnowledgeBaseEntryLike[]
  retrievedEntries: KnowledgeBaseEntryLike[]
} {
  const structuredKnowledgeBase = getRecordField(
    getRecordField(metricOutput.result, "llm"),
    "knowledge_base",
  )
  const knowledgeBase =
    structuredKnowledgeBase ??
    getRecordField(metricOutput.result_json, "knowledge_base")

  return {
    connectedEntries: getRecordArrayField<KnowledgeBaseEntryLike>(
      knowledgeBase,
      "connected_knowledge_base_entries",
    ),
    retrievedEntries: getRecordArrayField<KnowledgeBaseEntryLike>(
      knowledgeBase,
      "kb_entries_retrieved",
    ),
  }
}
